import { useCallback } from 'react';
import { FieldArray, useFormikContext } from 'formik';

import Alert from '@mui/material/Alert';
import Grid from '@mui/material/Grid';

import { getFragmentData } from '@e-socna/data-layer';
import type { FragmentType } from '@e-socna/data-layer';

import { ActorCardCmp_Organization_Fragment } from '../../../../components/Actors/Card/fragments/organization.fragment';

import type { ActorForm_Actor } from '../../fragments/actor.fragment';
import { requiredRepresentativeTypesRefusalOf } from '../../utils/required-representative-types.util';
import { requiredRolesRefusalOf } from '../../utils/required-roles.util';
import type { RepresentativeTypeRequirement } from '../../utils/required-representative-types.util';
import type { RoleRequirement } from '../../utils/required-roles.util';

import { RepresentativesDetails } from './RepresentativesDetails';
import { AlertStack } from '../../../../components/AlertStack';

interface Props {
  readonly id: string;
  readonly organization:
    FragmentType<typeof ActorCardCmp_Organization_Fragment> | undefined;
  readonly roleRequirement?: RoleRequirement;
  readonly typeRequirement?: RepresentativeTypeRequirement;
}

export const ActorRepresentativesDetailsFormStep = ({
  organization: pOrganization,
  roleRequirement,
  typeRequirement,
}: Readonly<Props>) => {
  const { values } = useFormikContext<ActorForm_Actor>();
  const organization = getFragmentData(
    ActorCardCmp_Organization_Fragment,
    pOrganization,
  );

  const memberships = organization?.allMemberships;

  const fieldArrayRender = useCallback(
    () => <RepresentativesDetails memberships={memberships} />,
    [memberships],
  );

  const representativeTypeRefusal =
    typeRequirement && values.__typename === 'ActorOrganization'
      ? requiredRepresentativeTypesRefusalOf(
          values,
          memberships ?? [],
          typeRequirement,
        )
      : null;

  const requiredRolesRefusal =
    roleRequirement && values.__typename === 'ActorOrganization'
      ? requiredRolesRefusalOf(values, memberships ?? [], roleRequirement)
      : null;

  return (
    <Grid container sx={{ display: 'flex', flexGrow: 1 }}>
      <Grid size={12}>
        <AlertStack>
          {[
            requiredRolesRefusal ? (
              <Alert key="requiredRoles" severity="error">
                {requiredRolesRefusal}
              </Alert>
            ) : null,
            representativeTypeRefusal ? (
              <Alert key="representativeTypes" severity="error">
                {representativeTypeRefusal}
              </Alert>
            ) : null,
          ].filter(Boolean)}
        </AlertStack>
      </Grid>
      <Grid size={12} sx={{ display: 'flex', flexDirection: 'column' }}>
        <FieldArray name="representatives" render={fieldArrayRender} />
      </Grid>
    </Grid>
  );
};
